import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Check } from 'lucide-react';
import { API } from '../../config/api';
import { useAuth } from '../../contexts';

// SVG Flags
const FlagItaly = () => (
  <svg width="20" height="14" viewBox="0 0 24 16" className="rounded-sm inline-block">
    <rect width="8" height="16" fill="#009246"/><rect x="8" width="8" height="16" fill="#fff"/><rect x="16" width="8" height="16" fill="#CE2B37"/>
  </svg>
);
const FlagFrance = () => (
  <svg width="20" height="14" viewBox="0 0 24 16" className="rounded-sm inline-block">
    <rect width="8" height="16" fill="#002395"/><rect x="8" width="8" height="16" fill="#fff"/><rect x="16" width="8" height="16" fill="#ED2939"/>
  </svg>
);
const FlagSpain = () => (
  <svg width="20" height="14" viewBox="0 0 24 16" className="rounded-sm inline-block">
    <rect width="24" height="4" fill="#AA151B"/><rect y="4" width="24" height="8" fill="#F1BF00"/><rect y="12" width="24" height="4" fill="#AA151B"/>
  </svg>
);
const FlagUK = () => (
  <svg width="20" height="14" viewBox="0 0 24 16" className="rounded-sm inline-block">
    <rect width="24" height="16" fill="#012169"/><path d="M0,0 L24,16 M24,0 L0,16" stroke="#fff" strokeWidth="2"/><path d="M12,0 V16 M0,8 H24" stroke="#fff" strokeWidth="3"/><path d="M12,0 V16 M0,8 H24" stroke="#C8102E" strokeWidth="2"/>
  </svg>
);

const getCountryFlag = (country) => {
  switch(country) {
    case 'Italia': return <FlagItaly />;
    case 'Francia': return <FlagFrance />;
    case 'Spagna': return <FlagSpain />;
    case 'UK': return <FlagUK />;
    default: return null;
  }
};

const UserModal = ({ user, teams, onClose, onSave }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const currentUser = auth?.user;
  const [formData, setFormData] = useState({
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    password: "",
    role: user?.role || "installer",
    team_id: user?.team_id || "",
    is_active: user?.is_active ?? true,
    starting_address: user?.starting_address || "",
    allowed_countries: user?.allowed_countries || [],
    lunch_start: user?.lunch_start || "",
    lunch_end: user?.lunch_end || "",
    // QC specific fields
    assigned_installer_ids: user?.assigned_installer_ids || [],
    assigned_team_ids: user?.assigned_team_ids || [],
    calendar_view_all: user?.calendar_view_all || false,
  });
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  // All installers for QC assignment
  const [allInstallers, setAllInstallers] = useState([]);
  const [showNewTeamForm, setShowNewTeamForm] = useState(false);
  const [newTeamData, setNewTeamData] = useState({ name: "", region: "" });
  const [creatingTeam, setCreatingTeam] = useState(false);

  const getAvailableRoles = () => {
    if (currentUser?.role === "superadmin") {
      return ["superadmin", "admin", "operator", "installer", "quality_control"];
    } else if (currentUser?.role === "admin") {
      return ["admin", "operator", "installer", "quality_control"];
    }
    return ["installer"];
  };

  const getAssignableCountries = () => {
    if (currentUser?.role === "superadmin") {
      return countries;
    }
    return currentUser?.allowed_countries || [];
  };

  useEffect(() => {
    fetchCountries();
    fetchAllInstallers();
    if (user?.team_id) {
      const existingTeam = teams.find(t => t.id === user.team_id);
      if (existingTeam) {
        setSelectedCountry(existingTeam.country);
      }
    }
  }, []);
  
  const fetchAllInstallers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      const installers = res.data.filter(u => u.role === "installer" && u.is_active);
      setAllInstallers(installers);
    } catch (e) {
      console.error("Error fetching installers:", e);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/reports/countries`);
      setCountries(res.data);
      if (!selectedCountry && res.data.length > 0) {
        setSelectedCountry(res.data[0]);
      }
    } catch (e) {
      console.error("Error fetching countries");
    }
  };

  const filteredTeams = teams.filter(t => t.country === selectedCountry);

  const handleCreateTeam = async () => {
    if (!newTeamData.name || !newTeamData.region) {
      toast.error(t('teams.messages.nameRegionRequired'));
      return;
    }
    setCreatingTeam(true);
    try {
      const res = await axios.post(`${API}/teams`, {
        name: newTeamData.name,
        region: newTeamData.region,
        country: selectedCountry,
        description: ""
      });
      toast.success(t('teams.messages.created'));
      setFormData({ ...formData, team_id: res.data.id });
      setShowNewTeamForm(false);
      setNewTeamData({ name: "", region: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.generic'));
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      if (!payload.team_id) payload.team_id = null;
      if (!payload.starting_address) payload.starting_address = null;

      if (user) {
        await axios.put(`${API}/users/${user.id}`, payload);
        toast.success(t('users.messages.updated'));
      } else {
        if (!payload.password) {
          toast.error(t('users.messages.passwordRequired') || "La password è obbligatoria per i nuovi utenti");
          setLoading(false);
          return;
        }
        await axios.post(`${API}/users`, payload);
        toast.success(t('users.messages.created'));
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {user ? t('users.editUser') : t('users.newUser')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.firstName')} *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
                required
                data-testid="user-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.lastName')} *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
                required
                data-testid="user-last-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.email')} *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              required
              data-testid="user-email"
            />
            {user && (
              <p className="text-xs text-gray-500 mt-1">
                ⚠️ Modificando l&apos;email, l&apos;utente dovrà usare la nuova email per accedere
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('users.password')} {user ? t('users.passwordHint') : "*"}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              required={!user}
              data-testid="user-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.role')} *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value, team_id: "", allowed_countries: [] })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
              required
              data-testid="user-role"
            >
              {getAvailableRoles().map(role => (
                <option key={role} value={role}>{t(`roles.${role}`)}</option>
              ))}
            </select>
          </div>

          {(formData.role === "admin" || formData.role === "operator") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('users.allowedCountries')} *
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {formData.role === "admin" ? t('users.allowedCountriesHintAdmin') : t('users.allowedCountriesHintOperator')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {getAssignableCountries().map((country) => {
                  const isSelected = formData.allowed_countries.includes(country);
                  return (
                    <button
                      key={country}
                      type="button"
                      onClick={() => {
                        const newAllowed = isSelected
                          ? formData.allowed_countries.filter(c => c !== country)
                          : [...formData.allowed_countries, country];
                        setFormData({ ...formData, allowed_countries: newAllowed });
                      }}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition ${
                        isSelected
                          ? 'border-lojack-red bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {getCountryFlag(country)}
                      <span className="text-sm font-medium">{t(`teams.countries.${country}`) || country}</span>
                      {isSelected && <Check size={16} className="text-lojack-red ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {formData.role === "installer" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('teams.country')} *</label>
                <div className="grid grid-cols-2 gap-2">
                  {countries.map((country) => (
                    <button
                      key={country}
                      type="button"
                      onClick={() => {
                        setSelectedCountry(country);
                        setFormData({ ...formData, team_id: "" });
                        setShowNewTeamForm(false);
                      }}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition ${
                        selectedCountry === country
                          ? 'border-lojack-red bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {getCountryFlag(country)}
                      <span className="text-sm font-medium">{t(`teams.countries.${country}`) || country}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.team')} *</label>
                {filteredTeams.length > 0 ? (
                  <select
                    value={formData.team_id}
                    onChange={(e) => {
                      if (e.target.value === "NEW") {
                        setShowNewTeamForm(true);
                        setFormData({ ...formData, team_id: "" });
                      } else {
                        setShowNewTeamForm(false);
                        setFormData({ ...formData, team_id: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
                    required={!showNewTeamForm}
                    data-testid="user-team"
                  >
                    <option value="">{t('appointment.selectTeam')}</option>
                    {filteredTeams.map((team) => (
                      <option key={team.id} value={team.id}>{team.name} - {team.region}</option>
                    ))}
                    <option value="NEW">➕ Crea nuovo team...</option>
                  </select>
                ) : (
                  <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                    Nessun team in {t(`teams.countries.${selectedCountry}`) || selectedCountry}.
                    <button
                      type="button"
                      onClick={() => setShowNewTeamForm(true)}
                      className="ml-2 text-lojack-red hover:underline font-medium"
                    >
                      Crea il primo team
                    </button>
                  </div>
                )}
              </div>

              {showNewTeamForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-blue-800">Crea nuovo team per {t(`teams.countries.${selectedCountry}`) || selectedCountry}</p>
                  <div>
                    <input
                      type="text"
                      value={newTeamData.name}
                      onChange={(e) => setNewTeamData({ ...newTeamData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Nome team (es: Installatori Lombardia)"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={newTeamData.region}
                      onChange={(e) => setNewTeamData({ ...newTeamData, region: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Regione/Zona (es: Lombardia)"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowNewTeamForm(false)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annulla
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateTeam}
                      disabled={creatingTeam}
                      className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {creatingTeam ? "Creazione..." : "Crea Team"}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.startingAddress')}</label>
                <input
                  type="text"
                  value={formData.starting_address}
                  onChange={(e) => setFormData({ ...formData, starting_address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
                  placeholder="Es: Via Roma 10, Milano, MI"
                  data-testid="user-starting-address"
                />
                <p className="text-xs text-gray-500 mt-1">{t('users.startingAddressHint')}</p>
              </div>

              {/* Lunch Break Settings */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  🍽️ Pausa Pranzo Personalizzata
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Lascia vuoto per usare l&apos;orario predefinito (12:30 - 13:30)
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Inizio pausa</label>
                    <input
                      type="time"
                      value={formData.lunch_start}
                      onChange={(e) => setFormData({ ...formData, lunch_start: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red text-sm"
                      placeholder="12:30"
                      data-testid="user-lunch-start"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fine pausa</label>
                    <input
                      type="time"
                      value={formData.lunch_end}
                      onChange={(e) => setFormData({ ...formData, lunch_end: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red text-sm"
                      placeholder="13:30"
                      data-testid="user-lunch-end"
                    />
                  </div>
                </div>
                {formData.lunch_start && formData.lunch_end && (
                  <p className="text-xs text-green-600 mt-2">
                    ✓ Pausa personalizzata: {formData.lunch_start} - {formData.lunch_end}
                  </p>
                )}
              </div>

              {/* Visibilita' estesa calendario (tecnico che fa anche controllo qualita') */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.calendar_view_all}
                    onChange={(e) => setFormData({ ...formData, calendar_view_all: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    data-testid="user-calendar-view-all"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">👁️ Visibilità estesa calendario</span>
                    <span className="block text-xs text-gray-600 mt-0.5">
                      Il tecnico vede il calendario di TUTTI gli installatori (come il backoffice), oltre al proprio.
                      Utile per tecnici che svolgono anche controllo qualità. Resta un installatore a tutti gli effetti:
                      riceve appuntamenti e mantiene la sua colonna nel calendario.
                    </span>
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Quality Control specific fields */}
          {formData.role === "quality_control" && (
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/50 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔍</span>
                <label className="text-sm font-semibold text-purple-800">
                  Assegnazione Quality Control
                </label>
              </div>
              <p className="text-xs text-purple-600 mb-3">
                Seleziona gli installatori e/o i team che questo utente QC potrà visualizzare.
              </p>
              
              {/* Assigned Teams */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Assegnati
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {teams.map((team) => (
                    <label
                      key={team.id}
                      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition ${
                        formData.assigned_team_ids.includes(team.id)
                          ? 'border-purple-500 bg-purple-100'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_team_ids.includes(team.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              assigned_team_ids: [...formData.assigned_team_ids, team.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              assigned_team_ids: formData.assigned_team_ids.filter(id => id !== team.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{team.name}</span>
                      <span className="text-xs text-gray-500">({team.region})</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Assigned Installers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Installatori Specifici Assegnati
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {allInstallers.map((installer) => (
                    <label
                      key={installer.id}
                      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition ${
                        formData.assigned_installer_ids.includes(installer.id)
                          ? 'border-purple-500 bg-purple-100'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_installer_ids.includes(installer.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              assigned_installer_ids: [...formData.assigned_installer_ids, installer.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              assigned_installer_ids: formData.assigned_installer_ids.filter(id => id !== installer.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {installer.first_name} {installer.last_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({teams.find(t => t.id === installer.team_id)?.name || 'Nessun team'})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Summary */}
              {(formData.assigned_team_ids.length > 0 || formData.assigned_installer_ids.length > 0) && (
                <div className="p-2 bg-purple-100 rounded-lg">
                  <p className="text-xs text-purple-800">
                    <strong>Riepilogo:</strong>{' '}
                    {formData.assigned_team_ids.length > 0 && (
                      <span>{formData.assigned_team_ids.length} team</span>
                    )}
                    {formData.assigned_team_ids.length > 0 && formData.assigned_installer_ids.length > 0 && ' + '}
                    {formData.assigned_installer_ids.length > 0 && (
                      <span>{formData.assigned_installer_ids.length} installatori</span>
                    )}
                    {' '}assegnati
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-lojack-red border-gray-300 rounded focus:ring-lojack-red"
              data-testid="user-active"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">{t('users.active')}</label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || (formData.role === "installer" && !formData.team_id && !showNewTeamForm)}
              className="px-6 py-2 bg-lojack-red text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              data-testid="user-save-btn"
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
